import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Handle redirect result on page load (for signInWithRedirect flow)
  useEffect(() => {
    getRedirectResult(auth).catch((err: unknown) => {
      const firebaseErr = err as { code?: string; message?: string };
      if (firebaseErr.code !== 'auth/popup-closed-by-user') {
        setError(firebaseErr.message || 'Sign-in failed');
      }
    });
  }, []);

  // Pick up 403 error from sessionStorage (set by axios response interceptor)
  useEffect(() => {
    const storedError = sessionStorage.getItem('auth_error');
    if (storedError) {
      setError(storedError);
      sessionStorage.removeItem('auth_error');
    }
  }, [user]);

  const signInWithGoogle = async () => {
    setError(null);
    try {
      // Try popup first — works in most browsers.
      // Falls back to redirect for environments that block popups or
      // 3rd-party cookies (incognito, Safari, some Chrome configs).
      await signInWithPopup(auth, googleProvider);
    } catch (err: unknown) {
      const firebaseErr = err as { code?: string; message?: string };
      if (firebaseErr.code === 'auth/popup-closed-by-user') {
        return; // User cancelled — not an error
      }
      // Popup blocked or cross-origin issue — fall back to redirect
      if (
        firebaseErr.code === 'auth/popup-blocked' ||
        firebaseErr.code === 'auth/cancelled-popup-request' ||
        firebaseErr.code === 'auth/internal-error' ||
        firebaseErr.code === 'auth/network-request-failed'
      ) {
        try {
          await signInWithRedirect(auth, googleProvider);
          return; // Page will redirect — this won't actually run
        } catch (redirectErr) {
          setError('Sign-in failed. Please try again.');
        }
      } else {
        setError(firebaseErr.message || 'Sign-in failed');
      }
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, signInWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
